/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";

import { OverflowTagList } from "../overflow_tag_list";
import {
  TagActionsWrapper,
  TagIconButton,
  TagName,
  TagPairLabel,
  TagRoot,
  TagTextWrapper,
} from "./styles";

const TEST_ID_PREFIX = "partners";

const RestaurantPartnerTag = React.memo(
  ({ item, canEdit, canDelete, onEdit, onDelete, isMeasurement = false }) => {
    return (
      <TagRoot
        aria-hidden={isMeasurement ? "true" : undefined}
        data-testid={isMeasurement ? undefined : `partner-tag-${item.id}`}
      >
        <TagTextWrapper>
          <TagName
            component="span"
            data-testid={isMeasurement ? undefined : `partner-tag-name-${item.id}`}
          >
            {item.name}
          </TagName>
          <TagPairLabel
            component="span"
            data-testid={isMeasurement ? undefined : `partner-tag-label-${item.id}`}
          >
            {item.label}
          </TagPairLabel>
        </TagTextWrapper>

        <TagActionsWrapper>
          {canEdit && (
            <TagIconButton
              size="small"
              onClick={() => onEdit?.(item)}
              data-testid={isMeasurement ? undefined : `partner-tag-edit-${item.id}`}
              aria-label={isMeasurement ? undefined : `Edit ${item.name}`}
              tabIndex={isMeasurement ? -1 : undefined}
            >
              <EditOutlinedIcon fontSize="inherit" />
            </TagIconButton>
          )}

          {canDelete && (
            <TagIconButton
              size="small"
              onClick={() => onDelete?.(item)}
              data-testid={isMeasurement ? undefined : `partner-tag-delete-${item.id}`}
              aria-label={isMeasurement ? undefined : `Delete ${item.name}`}
              tabIndex={isMeasurement ? -1 : undefined}
            >
              <CloseIcon fontSize="inherit" />
            </TagIconButton>
          )}
        </TagActionsWrapper>
      </TagRoot>
    );
  },
);

RestaurantPartnerTag.displayName = "RestaurantPartnerTag";

export const RenderRestaurantPartners = React.memo(
  ({
    data = [],
    canEdit = false,
    canDelete = false,
    onEdit,
    onDelete,
    emptyLabel = "No Partners",
  }) => {
    const renderTag = React.useCallback(
      (item, { isMeasurement }) => (
        <RestaurantPartnerTag
          item={item}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={onEdit}
          onDelete={onDelete}
          isMeasurement={isMeasurement}
        />
      ),
      [canEdit, canDelete, onEdit, onDelete],
    );

    return (
      <OverflowTagList
        data={data}
        renderTag={renderTag}
        emptyLabel={emptyLabel}
        testIdPrefix={TEST_ID_PREFIX}
        overflowAriaLabel={(count) => `${count} more partners`}
      />
    );
  },
);

RenderRestaurantPartners.displayName = "RenderRestaurantPartners";
